import { useEffect, useState } from 'react';
import { useLocation } from '@gatsbyjs/reach-router';
import { useFeatureConfig, useGetExperiment, type TExperiment } from '@printdeal/feature-management/react';

interface PagesVariable {
  [key: string]: unknown;
  pages?: string[];
  contentfulIds?: string[];
  excludedPages?: string[];
  excludedContentfulIds?: string[];
  data?: { contentfulId: string; [key: string]: unknown }[]; // data can be any object
}

type States = 'determining' | 'determined';

interface UsePagesExperimentResponse<T> {
  experiment: TExperiment | null;
  status: States;
  data: T | undefined;
}

/**
 * This function is used to check if the page is included in an experiment
 * This works by checking if the page id or page path is included in a feature variable
 * or explicitly excluded by a feature variable
 */
const isPageIncluded = (variable?: PagesVariable, path?: string, pageId?: string): boolean => {
  if (pageId) {
    if (variable?.excludedContentfulIds
        && Array.isArray(variable.excludedContentfulIds)
        && variable.excludedContentfulIds.length) {
      // If the page is explicitly excluded, return false
      return !variable.excludedContentfulIds.some((contentfulId) => contentfulId === pageId);
    }

    if (variable?.contentfulIds && Array.isArray(variable.contentfulIds) && variable.contentfulIds.length) {
      // If the page is explicitly included, return true
      return Boolean(variable.contentfulIds.some((contentfulId) => contentfulId === pageId));
    }
  }

  if (path) {
    if (variable?.excludedPages && Array.isArray(variable.excludedPages)) {
      // If the page is explicitly excluded, return false
      return !variable.excludedPages.some(path.includes);
    }

    if (variable?.pages && Array.isArray(variable.pages)) {
      // If the page is explicitly included, return true
      return Boolean(variable.pages.some(path.includes));
    }
  }

  // If no page id or path is provided or the variable is not setup properly
  return false;
};

export const usePagesExperiment = <T>(
  experimentName: string,
  configName: string,
  pageId?: string,
): UsePagesExperimentResponse<T> => {
  const location = useLocation();
  const getExperiment = useGetExperiment();
  const [experiment, setExperiment] = useState<TExperiment | null>(null);
  const [status, setStatus] = useState<States>('determining');
  const [pageData, setPageData] = useState<T | undefined>(undefined);
  const { featureConfig, isInitialized } = useFeatureConfig<PagesVariable>(configName);

  useEffect(() => {
    if (isInitialized) {
      try {
        const pageIncluded = isPageIncluded(featureConfig.variables, location.pathname, pageId);

        if (pageIncluded) {
          setExperiment(getExperiment(experimentName)?.experiment);
        }

        // If the feature config has data, find the page data based on the Contentful ID
        if (featureConfig.variables?.data) {
          const page = featureConfig.variables.data?.find((data) => data.contentfulId === pageId);
          if (page) {
            setPageData(page as T);
          }
        }

        setStatus('determined');
      } catch (error) {
        setStatus('determined');
      }
    }
  }, [isInitialized, location.pathname, pageId, featureConfig.variables, experimentName]);

  return {
    experiment,
    status,
    data: pageData,
  };
};
